import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  X, Calendar, Clock, MapPin, Wrench, DollarSign, Mail, 
  CheckCircle, AlertCircle, FileText, User, Phone, 
  ChevronRight, ChevronDown, ChevronUp, Edit, Trash2, Send, Camera, ExternalLink,
  Check, Clock3, Receipt, Loader2, Car, Home, Settings, PawPrint, Milestone, Smartphone, Star  
} from 'lucide-react';
import { formatCurrency, formatDate, getStatusColor } from '../../utils/helpers';
import { 
  industryUsesAssets, 
  getAssetFieldSchema, 
  parseAttributes,
  AssetTypes 
} from '../../config/assetTemplates';
import { useBusinessContext } from '../../contexts/BusinessContext';
import JobPhotos from './JobPhotos';
import MapLink from '../MapLink';
import ConfirmModal from '../ConfirmModal';
import jobPhotoService from '../../services/jobPhotoService';
import { jobsApi } from '../../services/api';

/**
 * JobDetailDrawer - Slide-out drawer showing full job details with photo management
 * 
 * Photo count fix v2: Uses ref to track current job ID and prevent stale updates.
 * Also includes fallback to photos list endpoint if summary fails.
 * 
 * v4: Removed Property Measurement section (measurements now done inline via QuoteCalculator)
 */
const JobDetailDrawer = ({
  isOpen,
  onClose,
  job,
  onEdit,
  onDelete,
  onComplete,
  onSendPrepEmail,
  onSendQuoteEmail,
  onSendQuoteSms,
  sendingEmail,
  sendingQuoteEmail,
  sendingQuoteSms,
  smsSettings,
}) => {
  const navigate = useNavigate();
  const { user, business } = useBusinessContext();
  const isOwner = user?.role === 'Owner';
  const industryType = business?.industryType || 'Other';
  
  // Photo state
  const [photoCount, setPhotoCount] = useState(null); // null = loading, 0+ = loaded
  const [photoCountLoading, setPhotoCountLoading] = useState(false);
  const [photosExpanded, setPhotosExpanded] = useState(false);
  
  // Other UI state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Review request state (manual, owner only)
  const [reviewInfo, setReviewInfo] = useState(null);
  const [reviewTapped, setReviewTapped] = useState(false);
  const [reviewSaving, setReviewSaving] = useState(false);
  const [reviewError, setReviewError] = useState('');
  
  // Track current job ID to prevent stale updates
  const currentJobIdRef = useRef(null);

  // Fetch photo count when drawer opens with a new job
  useEffect(() => {
    const fetchPhotoCount = async () => {
      // Only fetch if drawer is open and we have a job
      if (!isOpen || !job?.id) {
        return;
      }

      const jobId = job.id;
      currentJobIdRef.current = jobId;
      
      setPhotoCountLoading(true);
      setPhotoCount(null);

      try {
        // Try summary endpoint first
        let count = 0;
        try {
          const summaryResponse = await jobPhotoService.getJobPhotoSummary(jobId);
          // API returns totalCount, not totalPhotos
          count = summaryResponse.data?.totalCount || 0;
        } catch (summaryErr) {
          // Fallback: fetch actual photos and count them
          try {
            const photosResponse = await jobPhotoService.getJobPhotos(jobId);
            count = Array.isArray(photosResponse.data) ? photosResponse.data.length : 0;
          } catch (photosErr) {
            console.error('[JobDetailDrawer] Photos endpoint also failed:', photosErr);
            count = 0;
          }
        }

        // Only update if this is still the current job (prevent stale updates)
        if (currentJobIdRef.current === jobId) {
          setPhotoCount(count);
          
          // Auto-expand if there are photos
          if (count > 0) {
            setPhotosExpanded(true);
          }
        }
      } catch (err) {
        console.error('[JobDetailDrawer] Error fetching photo count:', err);
        if (currentJobIdRef.current === jobId) {
          setPhotoCount(0);
        }
      } finally {
        if (currentJobIdRef.current === jobId) {
          setPhotoCountLoading(false);
        }
      }
    };

    fetchPhotoCount();
  }, [isOpen, job?.id]);

  // Reset state when drawer closes
  useEffect(() => {
    if (!isOpen) {
      // Clear ref immediately
      currentJobIdRef.current = null;
      
      // Reset delete dialog immediately
      setShowDeleteConfirm(false);
      setDeleting(false);
      
      // Small delay to allow close animation
      const timer = setTimeout(() => {
        setPhotosExpanded(false);
        setPhotoCount(null);
        setPhotoCountLoading(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  // Handler for when JobPhotos component updates photo count
  const handlePhotoCountChange = useCallback((newCount) => {
    setPhotoCount(newCount);
  }, []);

  // Handle delete with confirmation
  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await onDelete(job.id);
      setShowDeleteConfirm(false);
      onClose();
    } catch (err) {
      console.error('Error deleting job:', err);
      setDeleting(false);
    }
  };

  // ---- Review request (manual, owner only; nothing here sends automatically) ----
  useEffect(() => {
    setReviewInfo(null);
    setReviewTapped(false);
    setReviewError('');

    if (!isOpen || !isOwner || !job?.id || job.status !== 'Completed') return;

    let cancelled = false;
    jobsApi.getReviewRequest(job.id)
      .then((res) => { if (!cancelled) setReviewInfo(res.data); })
      .catch((err) => console.error('[JobDetailDrawer] Review request check failed:', err));

    return () => { cancelled = true; };
  }, [isOpen, isOwner, job?.id, job?.status]);

  const formatReviewDate = (ts) => {
    if (!ts) return '';
    const d = new Date(ts.endsWith('Z') ? ts : ts + 'Z');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleReviewConfirmSent = async () => {
    setReviewSaving(true);
    setReviewError('');
    try {
      const res = await jobsApi.markReviewRequested(job.id);
      setReviewInfo({
        canRequest: false,
        reason: 'Review already requested.',
        alreadyRequestedAt: res.data.reviewRequestedAt,
      });
      setReviewTapped(false);
    } catch (err) {
      console.error('[JobDetailDrawer] Failed to mark review requested:', err);
      setReviewError(err.response?.data?.error || 'Could not save. Please try again.');
    } finally {
      setReviewSaving(false);
    }
  };

  const handleReviewClear = async () => {
    setReviewSaving(true);
    setReviewError('');
    try {
      await jobsApi.clearReviewRequested(job.id);
      const res = await jobsApi.getReviewRequest(job.id);
      setReviewInfo(res.data);
    } catch (err) {
      console.error('[JobDetailDrawer] Failed to clear review request:', err);
      setReviewError('Could not undo. Please try again.');
    } finally {
      setReviewSaving(false);
    }
  };

  if (!job) return null;

  // Parse zone info from breakdown if present
  const parsedBreakdown = (() => {
    try {
      return job.quoteBreakdownJson ? JSON.parse(job.quoteBreakdownJson) : null;
    } catch { return null; }
  })();
  const zoneInfo = parsedBreakdown?.zoneInfo || null;
  const baseTotal = parsedBreakdown?.baseTotal || null;
  const zoneOverridden = parsedBreakdown?.zoneOverridden === true;
  const zoneAdjustedTotal = parsedBreakdown?.total || null;

  // Format full address
  const formatAddress = () => {
    const parts = [job.address, job.city, job.state, job.zipCode].filter(Boolean);
    return parts.join(', ');
  };

  // Get service names
  const getServiceNames = () => {
    if (job.jobServices && job.jobServices.length > 0) {
      return job.jobServices
        .map(js => js.serviceType?.name || js.serviceTypeName)
        .filter(Boolean);
    }
    return [];
  };

  // Format time from ISO string
  const formatTime = (isoString) => {
    if (!isoString) return null;
    try {
      const date = new Date(isoString);
      if (date.getUTCHours() === 0 && date.getUTCMinutes() === 0) {
        return null;
      }
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return null;
    }
  };

  // Format datetime for email sent dates
  const formatSentDate = (dateString) => {
    if (!dateString) return '';
    try {
      const utcDateString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
      const date = new Date(utcDateString);
      return date.toLocaleString('en-US', { 
        month: 'short', 
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return '';
    }
  };

  // Check action availability
  const canSendPrepEmail = job.status === 'Scheduled' && !job.prepEmailSent && job.scheduledDate && job.customer?.email;
  const canSendQuoteEmail = isOwner && job.status === 'Quoted' && !job.quoteEmailSent && job.customer?.email && job.quoteAmount > 0 && job.jobServices?.length > 0;
const canSendQuoteSms = isOwner && job.status === 'Quoted' && !job.quoteSmsSent && job.customer?.phone && job.quoteAmount > 0 && job.jobServices?.length > 0 && smsSettings?.smsQuoteTextingEnabled && smsSettings?.smsNotificationsEnabled;
  const canComplete = job.status === 'Scheduled' || job.status === 'In Progress';

  const serviceNames = getServiceNames();
  const scheduledTime = formatTime(job.scheduledDate);
  const statusColor = getStatusColor(job.status);

  // Render photo count badge
  const renderPhotoCountBadge = () => {
    if (photoCountLoading || photoCount === null) {
      // Loading state with spinner
      return (
        <span className="flex items-center gap-1 text-xs text-gray-400">
          <Loader2 size={12} className="animate-spin" />
        </span>
      );
    }
    if (photoCount > 0) {
      return (
        <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium">
          {photoCount}
        </span>
      );
    }
    return (
      <span className="text-xs text-gray-400">None yet</span>
    );
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div 
        className={`fixed inset-y-0 right-0 w-full max-w-lg bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <span className={`badge ${statusColor}`}>
              {job.status}
            </span>
            {job.isInvoiced && (
              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full flex items-center gap-1">
                <Receipt size={12} />
                Invoiced
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto h-[calc(100vh-60px)] px-4 sm:px-6 py-4 space-y-6 pb-36">
          
          {/* Customer & Address Section */}
          <div className="space-y-3">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {job.customer?.name || 'Unknown Customer'}
                </h2>
                {job.customer?.phone && (
                  <a 
                    href={`tel:${job.customer.phone}`}
                    className="text-sm text-gray-500 hover:text-primary-600 flex items-center gap-1 mt-1"
                  >
                    <Phone size={14} />
                    {job.customer.phone}
                  </a>
                )}
              </div>
              {job.customer?.id && (
                <Link
                  to={`/customers/${job.customer.id}`}
                  onClick={onClose}
                  className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                >
                  View Customer
                  <ChevronRight size={14} />
                </Link>
              )}
            </div>

            {/* Address with Map Link */}
            <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
              <MapPin size={18} className="text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <MapLink 
                  address={job.address}
                  city={job.city}
                  state={job.state}
                  zipCode={job.zipCode}
                  className="text-gray-900 hover:text-primary-600"
                />
              </div>
            </div>
          </div>

{/* Asset Section - Industry dependent */}
          {job.customerAsset && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                {getAssetFieldSchema(job.customerAsset.assetType)?.displayName || 'Asset'}
              </h3>
              <AssetDisplayCard 
                asset={job.customerAsset} 
                mileageIn={job.mileageIn}
                behaviorNotes={job.behaviorNotes}
                serviceFormula={job.serviceFormula}
              />
            </div>
          )}

          {/* Schedule Section */}
          {(job.scheduledDate || job.completedDate) && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Schedule</h3>
              <div className="grid grid-cols-2 gap-3">
                {job.scheduledDate && (
                  <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
                    <Calendar size={18} className="text-blue-500" />
                    <div>
                      <p className="text-xs text-blue-600 font-medium">Scheduled</p>
                      <p className="text-sm text-gray-900">{formatDate(job.scheduledDate)}</p>
                      {scheduledTime && (
                        <p className="text-xs text-gray-500">{scheduledTime}</p>
                      )}
                    </div>
                  </div>
                )}
                {job.completedDate && (
                  <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg">
                    <CheckCircle size={18} className="text-green-500" />
                    <div>
                      <p className="text-xs text-green-600 font-medium">Completed</p>
                      <p className="text-sm text-gray-900">{formatDate(job.completedDate)}</p>
                    </div>
                  </div>
                )}
              </div>
              {job.estimatedDurationMinutes && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Clock3 size={14} />
                  Estimated: {job.estimatedDurationMinutes} minutes
                </div>
              )}
            </div>
          )}

          {/* Services Section */}
          {serviceNames.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Services</h3>
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                {job.jobServices?.map((js, idx) => (
                  <div key={js.id || idx} className="flex items-center justify-between p-3">
                    <div className="flex items-center gap-2">
                      <Wrench size={16} className="text-gray-400" />
                      <span className="text-gray-900">{js.serviceType?.name || js.serviceTypeName}</span>
                      {js.quantity > 1 && (
                        <span className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">×{js.quantity}</span>
                      )}
                    </div>
                    {isOwner && js.totalPrice > 0 && (
                      <span className="font-medium text-gray-900">{formatCurrency(js.totalPrice)}</span>
                    )}
                  </div>
                ))}
                {isOwner && zoneInfo && zoneInfo.pct !== 0 && (
                  <div className={`flex items-center justify-between px-3 py-2 border-t ${
                    zoneOverridden 
                      ? 'bg-gray-50 border-gray-100' 
                      : 'bg-red-50 border-red-100'
                  }`}>
                    <div className={`flex items-center gap-1.5 text-xs ${zoneOverridden ? 'text-gray-400' : 'text-red-700'}`}>
                      <MapPin size={12} />
                      <span className="font-medium">{zoneInfo.zoneName}</span>
                      <span className="opacity-70">· {zoneInfo.distanceMiles} mi ·</span>
                      {zoneOverridden
                        ? <span className="line-through">+{zoneInfo.pct}%</span>
                        : <span>+{zoneInfo.pct}% applied</span>
                      }
                      {zoneOverridden && <span className="italic">ignored</span>}
                    </div>
                    {!zoneOverridden && baseTotal && (
                      <span className="text-xs text-gray-400 line-through">{formatCurrency(baseTotal)}</span>
                    )}
                  </div>
                )}
                {isOwner && parsedBreakdown?.discountReason && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border-t border-green-100">
                    <span className="text-xs text-green-700">
                      {parsedBreakdown.discountReason === 'Veteran' ? '🎖️' :
                       parsedBreakdown.discountReason === 'Senior' ? '⭐' :
                       parsedBreakdown.discountReason === 'First Responder' ? '🚒' : '🤝'}
                      {' '}{parsedBreakdown.discountReason} Discount Applied
                    </span>
                  </div>
                )}
                {isOwner && job.quoteAmount > 0 && (
                  <div className="flex items-center justify-between p-3 bg-gray-50">
                    <span className="font-medium text-gray-700">Total</span>
                    <span className="text-lg font-semibold text-primary-600">
                      {formatCurrency(zoneAdjustedTotal || job.quoteAmount)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Pricing Section (Owner Only) - only show if finalAmount differs from quoteAmount */}
          {isOwner && job.finalAmount > 0 && job.finalAmount !== job.quoteAmount && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Pricing</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Quote Amount</p>
                  <p className="text-lg font-semibold text-gray-900">{formatCurrency(job.quoteAmount)}</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-xs text-green-600">Final Amount</p>
                  <p className="text-lg font-semibold text-green-700">{formatCurrency(job.finalAmount)}</p>
                </div>
              </div>
            </div>
          )}

          {/* Photos Section - Collapsible */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setPhotosExpanded(!photosExpanded)}
              className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Camera size={16} className="text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Photos</span>
                {renderPhotoCountBadge()}
              </div>
              {photosExpanded ? (
                <ChevronUp size={18} className="text-gray-400" />
              ) : (
                <ChevronDown size={18} className="text-gray-400" />
              )}
            </button>
            
            {photosExpanded && (
              <div className="p-4 border-t border-gray-200">
                <JobPhotos
                  jobId={job.id}
                  customerId={job.customerId}
                  onPhotoCountChange={handlePhotoCountChange}
                  readOnly={job.status === 'Cancelled'}
                />
              </div>
            )}
          </div>

          {/* Notes Section */}
          {(job.description || job.notes) && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Notes</h3>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                  {job.description || job.notes}
                </p>
              </div>
            </div>
          )}

          {/* Email History Section - Only show sent emails */}
          {(job.quoteEmailSent || job.confirmationEmailSent || job.prepEmailSent || job.completedEmailSent) && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Emails Sent</h3>
              <div className="space-y-2">
                {/* Quote Email */}
{isOwner && job.quoteEmailSent && (
  <div className="flex items-center justify-between p-3 rounded-lg bg-green-50">
    <div className="flex items-center gap-2">
      <Check size={16} className="text-green-500" />
      <span className="text-sm text-gray-700">Quote Email</span>
    </div>
    {job.quoteEmailSentDate && (
      <span className="text-xs text-gray-500">{formatSentDate(job.quoteEmailSentDate)}</span>
    )}
  </div>
)}

{/* Quote SMS */}
{isOwner && job.quoteSmsSent && (
  <div className="flex items-center justify-between p-3 rounded-lg bg-green-50">
    <div className="flex items-center gap-2">
      <Check size={16} className="text-green-500" />
      <span className="text-sm text-gray-700">Quote Text Sent</span>
    </div>
    {job.quoteSmsSentDate && (
      <span className="text-xs text-gray-500">{formatSentDate(job.quoteSmsSentDate)}</span>
    )}
  </div>
)}

                {/* Confirmation Email */}
                {job.confirmationEmailSent && (
                  <div className="flex items-center justify-between p-3 rounded-lg bg-green-50">
                    <div className="flex items-center gap-2">
                      <Check size={16} className="text-green-500" />
                      <span className="text-sm text-gray-700">Confirmation Email</span>
                    </div>
                    {job.confirmationEmailSentDate && (
                      <span className="text-xs text-gray-500">{formatSentDate(job.confirmationEmailSentDate)}</span>
                    )}
                  </div>
                )}

                {/* Prep Email */}
                {job.prepEmailSent && (
                  <div className="flex items-center justify-between p-3 rounded-lg bg-green-50">
                    <div className="flex items-center gap-2">
                      <Check size={16} className="text-green-500" />
                      <span className="text-sm text-gray-700">Prep Email</span>
                    </div>
                    {job.prepEmailSentDate && (
                      <span className="text-xs text-gray-500">{formatSentDate(job.prepEmailSentDate)}</span>
                    )}
                  </div>
                )}

                {/* Completed Email */}
                {job.completedEmailSent && (
                  <div className="flex items-center justify-between p-3 rounded-lg bg-green-50">
                    <div className="flex items-center gap-2">
                      <Check size={16} className="text-green-500" />
                      <span className="text-sm text-gray-700">Completion Email</span>
                    </div>
                    {job.completedEmailSentDate && (
                      <span className="text-xs text-gray-500">{formatSentDate(job.completedEmailSentDate)}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Review Request - manual, owner only, completed jobs */}
          {isOwner && job.status === 'Completed' && reviewInfo && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Review Request</h3>

              {reviewInfo.alreadyRequestedAt ? (
                <div className="flex items-center justify-between p-3 rounded-lg bg-green-50">
                  <div className="flex items-center gap-2">
                    <Check size={16} className="text-green-500" />
                    <span className="text-sm text-gray-700">
                      Review requested on {formatReviewDate(reviewInfo.alreadyRequestedAt)}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={handleReviewClear}
                    disabled={reviewSaving}
                    className="text-xs text-gray-500 hover:text-gray-700 underline disabled:opacity-50"
                  >
                    Undo
                  </button>
                </div>
              ) : reviewTapped ? (
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <p className="text-sm text-amber-900 mb-2">
                    Did you send the text to {job.customer?.name || 'the customer'}?
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleReviewConfirmSent}
                      disabled={reviewSaving}
                      className="flex-1 px-3 py-2 text-sm font-medium bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50"
                    >
                      {reviewSaving ? 'Saving...' : 'Yes, I sent it'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setReviewTapped(false)}
                      disabled={reviewSaving}
                      className="flex-1 px-3 py-2 text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg disabled:opacity-50"
                    >
                      Not yet
                    </button>
                  </div>
                </div>
              ) : reviewInfo.canRequest ? (
                <>
                  {/* Delay the state flip so the sms: navigation starts before React swaps this link out */}
                  <a
                    href={reviewInfo.smsHref}
                    onClick={() => setTimeout(() => setReviewTapped(true), 250)}
                    className="w-full px-3 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center justify-center gap-1.5"
                  >
                    <Star size={14} />
                    Request review
                  </a>
                  <p className="text-xs text-gray-500">Opens a text you can edit before sending.</p>
                </>
              ) : (
                <p className="text-sm text-gray-500">{reviewInfo.reason}</p>
              )}

              {reviewError && <p className="text-xs text-red-600">{reviewError}</p>}
            </div>
          )}

          {/* Contract Billing Notice */}
          {job.customer?.billingType && job.customer.billingType !== 'PerJob' && (
            <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
              <div className="flex items-center gap-2">
                <DollarSign size={16} className="text-purple-600" />
                <span className="text-sm font-medium text-purple-900">{job.customer.billingType} Billing</span>
              </div>
              <p className="text-xs text-purple-700 mt-1">
                This job will be included in the customer's {job.customer.billingType.toLowerCase()} invoice
                {job.customer.contractRate && ` at ${formatCurrency(job.customer.contractRate)}/${job.customer.billingType === 'Weekly' ? 'week' : job.customer.billingType === 'Monthly' ? 'month' : 'quarter'}`}.
              </p>
            </div>
          )}

          {/* Timestamps */}
          <div className="text-xs text-gray-400 space-y-1 pt-4 border-t border-gray-100">
            <p>Created: {new Date(job.createdAt).toLocaleString()}</p>
            {job.updatedAt && (
              <p>Last Updated: {new Date(job.updatedAt).toLocaleString()}</p>
            )}
          </div>
        </div>

        {/* Footer Actions - Compact on mobile */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-4 py-3 safe-area-pb">
          {/* Primary actions - contextual based on job status */}
          {(canComplete || canSendQuoteEmail || canSendPrepEmail) && (
            <div className="flex gap-2 mb-2">
              {canComplete && (
                <button
                  onClick={() => {
                    onClose();
                    onComplete(job);
                  }}
                  className="flex-1 px-3 py-2 text-sm font-medium bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center justify-center gap-1.5"
                >
                  <CheckCircle size={16} />
                  Complete
                </button>
              )}

              {canSendQuoteEmail && (
  <button
    onClick={() => onSendQuoteEmail(job.id)}
    disabled={sendingQuoteEmail === job.id}
    className="flex-1 px-3 py-2 text-sm font-medium bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center justify-center gap-1.5 disabled:opacity-50"
  >
    <Send size={14} />
    {sendingQuoteEmail === job.id ? '...' : 'Email Quote'}
  </button>
)}

{canSendQuoteSms && (
  <button
    onClick={() => onSendQuoteSms(job.id)}
    disabled={sendingQuoteSms === job.id}
    className="flex-1 px-3 py-2 text-sm font-medium bg-purple-700 hover:bg-purple-800 text-white rounded-lg flex items-center justify-center gap-1.5 disabled:opacity-50"
  >
    <Smartphone size={14} />
    {sendingQuoteSms === job.id ? '...' : 'Text Quote'}
  </button>
)}

              {canSendPrepEmail && (
                <button
                  onClick={() => onSendPrepEmail(job.id)}
                  disabled={sendingEmail === job.id}
                  className="flex-1 px-3 py-2 text-sm font-medium bg-primary-600 hover:bg-primary-700 text-white rounded-lg flex items-center justify-center gap-1.5 disabled:opacity-50"
                >
                  <Mail size={14} />
                  {sendingEmail === job.id ? '...' : 'Prep Email'}
                </button>
              )}
            </div>
          )}

          {/* Edit/Delete row - always visible */}
          <div className="flex gap-2">
            <button
              onClick={() => {
                onClose();
                onEdit(job);
              }}
              className="flex-1 px-3 py-2 text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg flex items-center justify-center gap-1.5"
            >
              <Edit size={14} />
              {isOwner ? 'Edit' : 'Reschedule'}
            </button>

            {isOwner && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={deleting}
                className="px-3 py-2 text-sm font-medium border border-red-200 text-red-600 hover:bg-red-50 rounded-lg flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Trash2 size={14} />
                Delete
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Job"
        message={`Are you sure you want to delete this job for ${job?.customer?.name || 'this customer'}? This action cannot be undone.`}
        confirmText="Delete Job"
        cancelText="Cancel"
        variant="danger"
        loading={deleting}
      />
    </>
  );
};

/**
 * Asset display card for job detail drawer
 */
const AssetDisplayCard = ({ asset, mileageIn, behaviorNotes, serviceFormula }) => {
  if (!asset) return null;
  
  const attrs = parseAttributes(asset.attributesJson);
  
  // Icon based on asset type
  const AssetIcon = {
    Vehicle: Car,
    Property: Home,
    Equipment: Settings,
    Pet: PawPrint,
    Person: User,
  }[asset.assetType] || Wrench;

  // Build summary based on type
  const getSummary = () => {
    switch (asset.assetType) {
      case 'Vehicle':
        const vehicleParts = [];
        if (attrs.year) vehicleParts.push(attrs.year);
        if (attrs.make) vehicleParts.push(attrs.make);
        if (attrs.model) vehicleParts.push(attrs.model);
        return vehicleParts.join(' ') || null;
        
      case 'Property':
        const propParts = [];
        if (attrs.squareFeet) propParts.push(`${attrs.squareFeet.toLocaleString()} sq ft`);
        if (attrs.propertyType) propParts.push(attrs.propertyType);
        return propParts.join(' • ') || null;
        
      case 'Pet':
        const petParts = [];
        if (attrs.breed) petParts.push(attrs.breed);
        if (attrs.weight) petParts.push(`${attrs.weight} lbs`);
        return petParts.join(' • ') || null;
        
      case 'Person':
        if (attrs.hairType) return attrs.hairType;
        return null;
        
      default:
        return null;
    }
  };

  const summary = getSummary();

  return (
    <div className="p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-200">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white rounded-lg border border-gray-200 flex items-center justify-center flex-shrink-0">
          <AssetIcon size={20} className="text-gray-500 sm:w-6 sm:h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-gray-900 truncate">{asset.name}</p>
          {summary && (
            <p className="text-sm text-gray-500 truncate">{summary}</p>
          )}
          
          {/* Vehicle-specific: License plate and mileage */}
          {asset.assetType === 'Vehicle' && (
            <div className="mt-2 flex flex-col sm:flex-row sm:flex-wrap gap-1 sm:gap-x-4 sm:gap-y-1 text-sm">
              {attrs.licensePlate && (
                <span className="text-gray-600">
                  <span className="text-gray-400">Plate:</span> {attrs.licensePlate}
                </span>
              )}
              {attrs.vin && (
                <span className="text-gray-600 truncate">
                  <span className="text-gray-400">VIN:</span> ...{attrs.vin.slice(-6)}
                </span>
              )}
              {mileageIn && (
                <span className="text-blue-600 inline-flex items-center gap-1 leading-none">
                  <Milestone size={14} className="flex-shrink-0 -mt-px" />
                  {mileageIn.toLocaleString()} mi
                </span>
              )}
            </div>
          )}

          {/* Property-specific: Gate code, measurements */}
          {asset.assetType === 'Property' && (
            <div className="mt-2 flex flex-col sm:flex-row sm:flex-wrap gap-1 sm:gap-x-4 text-sm">
              {attrs.gateCode && (
                <span className="text-gray-600">
                  <span className="text-gray-400">Gate:</span> {attrs.gateCode}
                </span>
              )}
              {attrs.drivewaySquareFeet && (
                <span className="text-gray-600">
                  <span className="text-gray-400">Driveway:</span> {attrs.drivewaySquareFeet.toLocaleString()} sq ft
                </span>
              )}
            </div>
          )}
          
          {/* Pet-specific: Temperament warning and behavior notes */}
          {asset.assetType === 'Pet' && (
            <>
              {attrs.temperament && (attrs.temperament === 'aggressive' || attrs.temperament === 'anxious') && (
                <div className={`mt-2 p-2 rounded text-xs flex items-center gap-1.5 ${
                  attrs.temperament === 'aggressive' 
                    ? 'bg-red-50 text-red-700 border border-red-200' 
                    : 'bg-amber-50 text-amber-700 border border-amber-200'
                }`}>
                  <AlertCircle size={14} />
                  {attrs.temperament === 'aggressive' ? 'Needs caution - Handle with care' : 'Anxious - Use gentle approach'}
                </div>
              )}
              {behaviorNotes && (
                <div className="mt-2 p-2 bg-purple-50 rounded text-sm text-purple-700 border border-purple-200">
                  <span className="font-medium">Today's notes:</span> {behaviorNotes}
                </div>
              )}
              {attrs.specialInstructions && (
                <div className="mt-2 p-2 bg-blue-50 rounded text-sm text-blue-700 border border-blue-200">
                  <span className="font-medium">Standing instructions:</span> {attrs.specialInstructions}
                </div>
              )}
            </>
          )}
          
          {/* Person-specific: Allergies warning and service formula */}
          {asset.assetType === 'Person' && (
            <>
              {attrs.allergies && (
                <div className="mt-2 p-2 bg-red-50 rounded text-xs text-red-700 border border-red-200 flex items-start gap-1.5">
                  <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                  <span><span className="font-medium">Allergies:</span> {attrs.allergies}</span>
                </div>
              )}
              {serviceFormula && (
                <div className="mt-2 p-2 bg-pink-50 rounded text-sm text-pink-700 border border-pink-200">
                  <span className="font-medium">Formula used:</span> {serviceFormula}
                </div>
              )}
              {attrs.lastColorFormula && !serviceFormula && (
                <div className="mt-2 p-2 bg-gray-100 rounded text-sm text-gray-600">
                  <span className="font-medium">Last formula:</span> {attrs.lastColorFormula}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobDetailDrawer;